import { FC, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Badge, Button, Flex, theme, Typography } from 'antd';
import cn from 'classnames';

import ReadIcon from 'assets/images/icons/read.svg?react';
import { NotificationCard, useGetNotificationsCountQuery, useGetNotificationsPopupQuery } from 'entities/Notification';
import { useReadNotifications } from 'entities/Notification/hooks';
import { useCommonSlice, useInvalidateBrands, useMedia } from 'shared/hooks';
import { EMainRoutes } from 'shared/typings';
import { EmptyState, OverlayLoader } from 'shared/ui';

import styles from './NotificationsPopup.module.scss';

interface INotificationsPopupProps extends HTMLAttributes<HTMLDivElement> {}

export const NotificationsPopup: FC<INotificationsPopupProps> = ({ className, ...props }) => {
	const { t } = useTranslation();
	const {
		token: { colorBorderSecondary },
	} = theme.useToken();
	const isMd = useMedia();
	const navigate = useNavigate();
	const { appendReadNotification, setRead } = useReadNotifications();

	const { data: notifications, isFetching } = useGetNotificationsPopupQuery();
	const { data: unread } = useGetNotificationsCountQuery();
	const { clearInfinityScroll, setNotificationsModalIsOpened } = useCommonSlice();

	const onNavigate = (route: string) => {
		clearInfinityScroll();
		setNotificationsModalIsOpened(false);
		navigate(route);
	};

	useInvalidateBrands();

	return (
		<Flex {...props} vertical className={cn(className, styles.notificationsPopup)}>
			<Flex
				vertical={isMd}
				gap={12}
				justify="space-between"
				align="center"
				className={styles.header}
				style={{ borderColor: colorBorderSecondary }}>
				<Flex align="center" gap={8}>
					<Typography.Title level={5} style={{ margin: 0 }}>
						{t('Уведомления')}
					</Typography.Title>
					{!!unread?.unread_count && <Badge count={unread.unread_count} />}
				</Flex>
				<Button size="small" type="text" icon={<ReadIcon />} iconPosition="end" onClick={() => setRead({ ids: [] })}>
					{t('Прочитать все')}
				</Button>
			</Flex>

			<div className={styles.body}>
				{isFetching && <OverlayLoader />}

				{!!notifications?.count &&
					notifications?.results?.map((notification) => (
						<NotificationCard
							key={notification.id}
							notification={notification}
							appendReadNotification={appendReadNotification}
							size="sm"
						/>
					))}

				{!notifications?.count && (
					<EmptyState
						title={t('У вас пока нет уведомлений')}
						buttonText={t('В каталог')}
						buttonTo={`/${EMainRoutes.CATALOG}`}
					/>
				)}
			</div>

			{!!notifications?.count && (
				<div className={styles.footer} style={{ borderColor: colorBorderSecondary }}>
					<Button type="primary" size="small" block onClick={() => onNavigate(`/${EMainRoutes.NOTIFICATIONS}`)}>
						{t('Показать все уведомления')}
					</Button>
				</div>
			)}
		</Flex>
	);
};
